import { FC } from 'react';
import { DataSource, DataSourceCrmConfig } from '@/models/dataSource';
import { useTranslation } from 'react-i18next';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useUpdateDataSourceMutation } from '@/services/dataSources';
import { StepPageActions } from '@/global/components';
import { useAppSelector } from '@/reducers';
import { CREATE_DATA_SOURCE_CRM } from '../../constants';
import { CheckInstallationMessage } from './CheckInstallationMessage';
import { CheckInstallationParametersList } from './CheckInstallationParametersList';
import { CheckInstallationFieldsList } from './CheckInstallationFieldsList';

type CheckInstallationStepProps = {
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
  onGoToStep?: (id: CREATE_DATA_SOURCE_CRM) => void;
  onCancel?: VoidFunction;
};

export const CheckInstallationStep: FC<CheckInstallationStepProps> = ({ onNextStep, onPrevStep }) => {
  const workspace = useWorkspace();
  const { t } = useTranslation('data_source_edit');

  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceCrmConfig>> | undefined;
  const parameters = useAppSelector((state) => state.dataSourceIntegration.parameters);
  const fields = (useAppSelector((state) => state.dataSourceIntegration.fields) || []).filter((field) => field.checked);

  const [updateDataSource, { isLoading: isUpdateLoading }] = useUpdateDataSourceMutation();

  const handleNextStep = async () => {
    if (dataSource && dataSource.id) {
      const response = await updateDataSource({
        workspaceId: workspace.id,
        dataSourceId: dataSource.id,
        dataSource: {
          activate: true,
        },
      });

      if ('data' in response) {
        onNextStep();
      }
    }
  };

  return (
    <>
      <div className='relative mt-6 flex h-auto flex-col gap-4 rounded-lg border border-solid border-gray-200 p-6 text-left'>
        <div className='flex flex-col gap-2'>
          <h1 className='text-base font-bold leading-5 text-gray-900'>{t('crm.check_installation.title')}</h1>
          <CheckInstallationMessage>
            {t('crm.check_installation.source_name')}: {dataSource?.name}
          </CheckInstallationMessage>
        </div>

        <div>
          <CheckInstallationMessage>{t('crm.check_installation.parameters')}:</CheckInstallationMessage>
          <CheckInstallationParametersList listItems={parameters} />
        </div>

        <div>
          <CheckInstallationMessage>{t('crm.check_installation.fields')}:</CheckInstallationMessage>
          <CheckInstallationFieldsList listItems={fields} />
        </div>
      </div>

      <StepPageActions
        primaryButton={{
          text: 'COMMON.confirm',
          onClick: handleNextStep,
          isLoading: isUpdateLoading,
        }}
        secondaryButton={{
          text: 'COMMON.cancel',
          onClick: onPrevStep,
        }}
      />
    </>
  );
};
